import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';

import { TrackedButton } from '@Components/ui-v2/tracked-button';

interface ModalLogoutProps {
  isOpen: boolean;
  onCancel: () => void;
  onLogout: () => void;
}

const ModalLogout = ({ isOpen, onCancel, onLogout }: ModalLogoutProps) => {
  const [outlineWidth, setOutlineWidth] = useState(1);
  useEffect(() => {
    if (isOpen) setOutlineWidth(1);
  }, [isOpen]);

  // On clicking outside, animate the outline from 6px to 1px
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setOutlineWidth(6);
      setTimeout(() => setOutlineWidth(1), 300);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          style={{ background: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-1"
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}>
          <motion.div
            className="bg-white w-[320px] min-h-[120px] p-8 flex flex-col items-center justify-center shadow-xl outline outline-[#CCCCCC] transition-all duration-300 m-5"
            style={{ outlineWidth: `${outlineWidth}px` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}>
            <div className="w-full flex flex-col items-center justify-center gap-3 py-5">
              <span className="text-lg text-center font-semibold text-slate-800 mb-4">
                {t('User.UserModalLogoutMessage')}
              </span>
              <div className="flex gap-2 justify-center w-full sm:flex-row flex-col">
                <TrackedButton
                  variant="outline"
                  buttonName="CANCEL_LOGOUT"
                  onClick={onCancel}
                  isSubmit={false}
                  className="w-full bg-white">
                  {t('Credentials.Cancel')}
                </TrackedButton>
                <TrackedButton buttonName="CONFIRM_LOGOUT" onClick={onLogout} isSubmit={false} className="w-full">
                  {t('Sidebar.Logout')}
                </TrackedButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
};

export default ModalLogout;
